import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { waLink } from "@/lib/brand";
import { brl } from "@/components/shop/theme";

const STATUS = {
  PAID: { icon: CheckCircle2, t: "Pagamento confirmado!", d: "Recebemos seu pagamento. Já vamos preparar o envio pelos Correios.", c: "text-emerald-400" },
  SHIPPED: { icon: CheckCircle2, t: "Pedido enviado!", d: "Seu pedido já está a caminho.", c: "text-emerald-400" },
  PENDING: { icon: Clock, t: "Aguardando pagamento", d: "Assim que o Asaas confirmar o pagamento, esta página atualiza sozinha.", c: "text-amber-300" },
  CASH_PENDING: { icon: CheckCircle2, t: "Pedido recebido!", d: "Pagamento em dinheiro na retirada via moto táxi. Chame a gente no WhatsApp para combinar.", c: "text-emerald-400" },
  CANCELED: { icon: XCircle, t: "Pagamento cancelado", d: "O pagamento não foi concluído. Você pode tentar de novo.", c: "text-red-400" },
  EXPIRED: { icon: XCircle, t: "Pagamento expirado", d: "O prazo para pagar expirou. Faça um novo pedido.", c: "text-red-400" },
};

export default function Pedido() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    let tries = 0;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (!alive) return;
        setO(data);
        if (data?.status === "PENDING" && tries++ < 24) setTimeout(load, 5000);
      } catch { if (alive) setMissing(true); }
    };
    load();
    return () => { alive = false; };
  }, [id]);

  const s = STATUS[o?.status] ?? STATUS.PENDING;
  const Icon = s.icon;
  return (
    <main data-testid="order-page" className="grid min-h-screen place-items-center bg-[#07080f] px-5 py-16 text-white">
      <div className="w-full max-w-md rounded-[24px] border border-white/10 bg-[#0e1120] p-8">
        <a href="/" className="flex items-center gap-3"><img src="/brand/speedmax-icon.png" alt="" className="h-8 w-8" /><span className="font-display font-bold">SPEED MAX</span></a>
        {missing ? <p data-testid="order-missing" className="mt-8 text-zinc-300">Pedido não encontrado.</p> : !o ? <p className="mt-8 text-zinc-400">Carregando...</p> : (
          <>
            <Icon className={`mt-8 h-10 w-10 ${s.c}`} />
            <h1 data-testid="order-status-title" className="mt-4 font-display text-2xl font-bold">{s.t}</h1>
            <p className="mt-2 text-sm text-zinc-300">{s.d}</p>
            <div className="mt-6 space-y-1 border-t border-white/10 pt-4 text-sm">
              <p className="flex justify-between"><span className="text-zinc-400">Pedido</span><span>#{id.slice(0, 8).toUpperCase()}</span></p>
              <p className="flex justify-between"><span className="text-zinc-400">Produto</span><span>{o.quantity}x {o.product}</span></p>
              <p className="flex justify-between"><span className="text-zinc-400">Frete</span><span>{o.shipping?.service} · {o.shipping?.service_id === 0 ? "a combinar" : brl(o.shipping?.price)}</span></p>
              <p className="flex justify-between font-bold"><span>Total</span><span>{brl(o.total)}</span></p>
            </div>
            {o.status === "PENDING" && o.checkout_url && <a data-testid="order-pay-again" href={o.checkout_url} className="mt-6 block rounded-full grad-bg py-3 text-center text-sm font-semibold">Abrir pagamento</a>}
            <a data-testid="order-whatsapp" href={waLink(`Olá! Fiz o pedido #${id.slice(0, 8).toUpperCase()} (${o.quantity}x ${o.product}) pelo site${o.status === "CASH_PENDING" ? " para retirada via moto táxi com pagamento em dinheiro" : ""}.`)} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-sm hover:bg-white/5"><MessageCircle className="h-4 w-4" />Falar sobre o pedido</a>
          </>
        )}
      </div>
    </main>
  );
}
